import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import usmApi, { USM_TOKEN_KEY } from "@/lib/usmApi";

/**
 * UsmAuthContext — completely isolated from storefront AuthContext.
 *
 * Token lives in localStorage[USM_TOKEN_KEY]. On mount we attempt /auth/me to
 * hydrate the session. The Layout uses `employee` and `permissions` to gate
 * UI; the backend re-checks on every request.
 */
const UsmAuthContext = createContext(null);

export function UsmAuthProvider({ children }) {
  const [employee, setEmployee] = useState(null);
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem(USM_TOKEN_KEY);
      if (!token) { setEmployee(null); setBranch(null); return; }
      const r = await usmApi.get("/auth/me");
      setEmployee(r.data.employee);
      setBranch(r.data.branch);
    } catch (e) {
      setEmployee(null);
      setBranch(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (username, password) => {
    const r = await usmApi.post("/auth/login", { username, password });
    localStorage.setItem(USM_TOKEN_KEY, r.data.access_token);
    await refresh();
    return r.data;
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await usmApi.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem(USM_TOKEN_KEY);
    setEmployee(null);
    setBranch(null);
  }, []);

  const hasPerm = useCallback((perm) => {
    if (!employee) return false;
    return (employee.permissions || []).includes(perm);
  }, [employee]);

  const isSuperAdmin = !!employee && employee.role === "super_admin";

  return (
    <UsmAuthContext.Provider value={{ employee, branch, loading, login, logout, refresh, hasPerm, isSuperAdmin }}>
      {children}
    </UsmAuthContext.Provider>
  );
}

export const useUsmAuth = () => {
  const ctx = useContext(UsmAuthContext);
  if (!ctx) throw new Error("useUsmAuth must be used inside <UsmAuthProvider>");
  return ctx;
};
